import _ from 'lodash';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  ConfirmDialog, captureException, useFeatureFlags,
} from '@playbookux/playbook-web-core';
import { MemberDropdown } from './components/member-dropdown';
import {
  Circle,
  ContainerItemColumn,
  ErrorMessage,
  Link,
  Message,
  Status,
  TextEmail,
  Table,
  Header,
  Body,
  Row,
  Column,
  HeaderCell,
  Popup,
  ProjectApprovalContainer,
  ProjectApprovalText,
  TextName,
} from './styles';

import {
  DELETE_COMPANY_MEMBER,
  GET_COMPANY_BY_ID,
  UPDATE_COMPANY_MEMBER,
  updateCompanyMemberDeleted,
} from '../graphql';
import {
  INVALID_VALUE_ERROR, MEMBERS_NOT_FOUND, approvalOptions, statusOptions,
} from './constants';
import UpgradeButtonProjectApproval from './components/upgrade-button-project-approval';

const approvalMap = {
  "Doesn't need approval": 'no_approval_needed',
  Approver: 'approver',
  'Needs approval': 'needs_approval',
};

export const COMPANY_PLANS = {
  PAY_AS_YOU_GO: 'pay_as_you_go',
  GROW: 'grow',
  SCALE: 'scale',
};

const CompanyMembers = (props) => {
  const {
    companyId,
    data,
    meId,
    meRole,
    resendInvite,
  } = props;

  const [memberId, setMemberId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [removeMemberError, setRemoveMemberError] = useState(false);
  const [roleChangeError, setRoleChangeError] = useState(false);
  const [projectApprovalChangeError, setProjectApprovalChangeError] = useState(false);

  const [updateCompanyMembers] = useMutation(UPDATE_COMPANY_MEMBER);
  const [deleteCompanyMember] = useMutation(DELETE_COMPANY_MEMBER, {
    update: updateCompanyMemberDeleted(companyId, memberId),
  });

  const handleOpenDialog = (id) => {
    setShowConfirm(true);
    setMemberId(id);
  };

  const handleCloseDialog = () => {
    setShowConfirm(false);
  };

  const handleDeleteMember = async () => {
    handleCloseDialog();

    try {
      const id = memberId;
      await deleteCompanyMember({
        variables: {
          id,
        },
      });

      setRoleChangeError(false);
      setRemoveMemberError(false);
    } catch (err) {
      setRemoveMemberError(true);
      setErrorMessage(err.graphQLErrors[0].message);
    }
  };

  const handleRoleChange = (roleType, targetUserId, newRole, projectApproval) => {
    const role = _.lowerFirst(roleType.innerText);

    if (!role.includes(newRole)) {
      updateCompanyMembers({
        variables: {
          companyId,
          targetUserId,
          role,
          projectApproval,
        },
      })
        .then(() => {
          setRoleChangeError(false);
          setRemoveMemberError(false);
        })
        .catch((errors) => {
          if (errors.graphQLErrors[0].message !== INVALID_VALUE_ERROR) {
            setRoleChangeError(true);
            setErrorMessage(errors.graphQLErrors[0].message);
          }
        });
    }
  };

  const mapProjectApproval = (projectApprovalType) => approvalMap[projectApprovalType];

  const handleProjectApprovalChange = useMemo(() => async (projectApprovalType, targetUserId, role, newProjectApprovalType) => {
    const projectApproval = mapProjectApproval(projectApprovalType.textContent);

    if (!projectApproval?.includes(newProjectApprovalType)) {
      try {
        await updateCompanyMembers({
          variables: {
            companyId,
            targetUserId,
            role,
            projectApproval,
          },
        });
        setProjectApprovalChangeError(false);
        setRemoveMemberError(false);
      } catch (error) {
        setProjectApprovalChangeError(true);
        captureException('UPDATE_COMPANY_MEMBER_PROJECT_APPROVAL', error);
      }
    }
  },
  [companyId, updateCompanyMembers]);

  if (errorMessage) {
    captureException('COMPANY_MEMBERS_FAILED', errorMessage);
  }

  const canSend = (lastSendDate) => {
    if (new Date() - new Date(lastSendDate) > 604800000) {
      return true;
    }
    return false;
  };

  const {
    companyMembers,
    companyInvites,
  } = data;

  const {
    featureFlags: { projectApprovals },
  } = useFeatureFlags(companyId);

  const { data: { company } = {} } = useQuery(GET_COMPANY_BY_ID, {
    variables: { id: companyId },
    skip: !companyId,
  });
  const isCompanyPayAsYouGo = company?.plan === COMPANY_PLANS.PAY_AS_YOU_GO;

  return (
    <>
      {(roleChangeError || removeMemberError || projectApprovalChangeError) && (
        <ErrorMessage>
          {errorMessage}
        </ErrorMessage>
      )}

      {_.isEmpty(companyInvites) && _.isEmpty(companyMembers) ? (
        <Message>
          {MEMBERS_NOT_FOUND}
        </Message>
      ) : (
        <Table basic="very" textAlign="left">
          <Header>
            <Row>
              <HeaderCell>Name</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              {meRole === 'admin' ? (
                <HeaderCell>Role</HeaderCell>
              ) : (
                <HeaderCell />
              )}
              {(meRole === 'admin') ? (
                <HeaderCell>
                  <ProjectApprovalContainer>
                    <ProjectApprovalText>Project approval</ProjectApprovalText>
                    {(!projectApprovals) && (
                      <UpgradeButtonProjectApproval />
                    )}
                  </ProjectApprovalContainer>
                </HeaderCell>
              ) : (
                <HeaderCell />
              )}
              <HeaderCell />
              <HeaderCell />
            </Row>
          </Header>

          <Body>
            <>
              {!_.isEmpty(companyInvites) && companyInvites.map(({
                id, email, status, lastSend,
              }) => (
                <Row key={id}>
                  {/* Name Column */}
                  <Column />
                  {/* Email Column */}
                  <Column>
                    <TextEmail>{email}</TextEmail>
                  </Column>
                  {/* Status Column */}
                  <Column>
                    <ContainerItemColumn>
                      <Circle status={status} />
                      <Status>{status}</Status>
                    </ContainerItemColumn>
                  </Column>
                  {/* Role Column */}
                  <Column />
                  {/* Project Approval Columns */}
                  <Column />
                  {meRole === 'admin' && id !== meId && (
                    <>
                      {/* Remove and Resend Invite Columns */}
                      <Column>
                        <Link
                          remove
                          onClick={() => handleOpenDialog(id)}
                          color="#ED3A4E"
                        >
                          Remove
                        </Link>
                      </Column>
                      <Column>
                        {canSend(lastSend) ? (
                          <Link
                            onClick={() => resendInvite(email)}
                          >
                            Resend invite
                          </Link>
                        ) : (
                          <Popup
                            trigger={(
                              <Link
                                disabled
                              >
                                Resend invite
                              </Link>
                            )}
                            content={`An invite was sent on ${new Date(lastSend)
                              .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                              }. You can resend invites every 7 days`}
                            position="top center"
                          />
                        )}
                      </Column>
                    </>
                  )}
                </Row>
              ))}
              {!_.isEmpty(companyMembers) && companyMembers.map(({
                id,
                user,
                role,
                projectApproval,
              }) => (
                <Row key={id}>
                  <Column>
                    <TextName title={`${user.profile.firstName} ${user.profile.lastName}`}>
                      {user.profile.firstName}
                      {' '}
                      {user.profile.lastName}
                    </TextName>
                  </Column>

                  <Column>
                    <TextEmail title={user.email}>{user.email}</TextEmail>
                  </Column>

                  <Column>
                    <ContainerItemColumn>
                      <Circle status="accepted" />
                      <Status>accepted</Status>
                    </ContainerItemColumn>
                  </Column>
                  {(meRole === 'admin') ? (
                    <>
                      <Column>
                        {(user.id !== meId) ? (
                          <MemberDropdown
                            onChange={(target) => handleRoleChange(target, user.id, role, projectApproval)}
                            options={statusOptions}
                            value={role}
                          />
                        ) : `${role.charAt(0).toUpperCase() + role.slice(1)}`}
                      </Column>
                      <Column projectApprovals={!projectApprovals} isCompanyPayAsYouGo={isCompanyPayAsYouGo}>
                        <MemberDropdown
                          onChange={(target) => handleProjectApprovalChange(target, user.id, role, projectApproval)}
                          options={approvalOptions}
                          value={projectApproval}
                          disabled={!projectApprovals}
                        />
                      </Column>
                      <Column>
                        {(user.id !== meId) ? (
                          <Link
                            remove
                            onClick={() => handleOpenDialog(id)}
                            color="#ED3A4E"
                          >
                            Remove
                          </Link>
                        ) : ''}
                      </Column>
                      <Column />
                    </>
                  ) : (
                    <>
                      <Column />
                      <Column />
                    </>
                  )}
                </Row>
              ))}
            </>
          </Body>
        </Table>
      )}
      <ConfirmDialog
        show={showConfirm}
        title="Are you sure you would like to remove this person?"
        warning="Warning: This cannot be undone"
        confirmText="Remove"
        cancelText="Cancel"
        handleConfirm={handleDeleteMember}
        handleClose={handleCloseDialog}
      />
    </>
  );
};

CompanyMembers.propTypes = {
  companyId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    companyInvites: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        email: PropTypes.string,
        status: PropTypes.string,
      }),
    ),
    companyMembers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        projectApproval: PropTypes.string,
        user: PropTypes.shape({
          id: PropTypes.string,
          email: PropTypes.string,
          profile: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
          }),
        }),
      }),
    ),
  }).isRequired,
  meId: PropTypes.string.isRequired,
  meRole: PropTypes.string.isRequired,
  resendInvite: PropTypes.func.isRequired,
};

export default CompanyMembers;
